/* Expense > Receipt Inbox (Phase 1 = web upload). Wired to /v1/expense/receipts. Upload a
   receipt image/PDF; the bytes go to Azure Blob server-side and the metadata + content hash to
   the DB. Content-addressed, so re-uploading the same image is de-duplicated (returns the
   existing receipt flagged Duplicate). Email + SMS intake (service-engine channels) and
   auto-matching land in Phase 2 — noted inline so the surface is honest about its scope. */
import { UploadSimpleIcon } from '@phosphor-icons/react';
import { type ChangeEvent, useRef, useState } from 'react';
import {
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@andavo/web-ui';

import { useReceiptInbox, useUploadReceipt } from './hooks/use-receipts';
import { formatDate } from './lib/format';
import { ReceiptBadge } from './receiptBadge';

export function ReceiptInboxScreen() {
  const { data: receipts = [], isLoading, isError } = useReceiptInbox();
  const upload = useUploadReceipt();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    upload.mutate(
      { file },
      {
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Upload failed.'),
        onSettled: () => {
          if (inputRef.current) inputRef.current.value = '';
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
        <div className="flex flex-col gap-1">
          <h2 className="font-sans font-semibold text-content-primary text-base">
            Upload a receipt
          </h2>
          <p className="font-inter text-content-secondary text-sm">
            JPG, PNG, HEIC or PDF. Forwarding by email and text arrives in a
            later release.
          </p>
        </div>
        <div>
          <input
            ref={inputRef}
            id="receipt-file"
            type="file"
            accept="image/*,application/pdf"
            aria-label="Upload receipt"
            className="sr-only"
            onChange={onFile}
          />
          <Button
            variant="primary"
            size="sm"
            disabled={upload.isPending}
            onClick={() => inputRef.current?.click()}
          >
            <UploadSimpleIcon className="me-2 size-icon-sm" />
            {upload.isPending ? 'Uploading…' : 'Choose file'}
          </Button>
        </div>
      </Card>

      {error && (
        <p className="font-inter text-content-danger text-sm">{error}</p>
      )}
      {upload.data?.matchState === 'DUPLICATE' && (
        <p className="font-inter text-content-secondary text-sm">
          That receipt was already uploaded — kept the original, skipped the
          copy.
        </p>
      )}

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Uploaded</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Filed to</TableHead>
              <TableHead>State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {receipts.map((r) => (
              <TableRow key={r.receiptId}>
                <TableCell className="text-content-secondary">
                  {formatDate(r.uploadedAt)}
                </TableCell>
                <TableCell className="text-content-primary">
                  {r.contentType}
                </TableCell>
                <TableCell className="text-content-secondary">
                  {r.source}
                </TableCell>
                <TableCell className="text-content-secondary">
                  {r.reportId != null ? `Report #${r.reportId}` : '—'}
                </TableCell>
                <TableCell>
                  <ReceiptBadge state={r.matchState} />
                </TableCell>
              </TableRow>
            ))}
            {isLoading && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={5}
                  className="py-10 text-center text-content-secondary"
                >
                  Loading receipts…
                </TableCell>
              </TableRow>
            )}
            {isError && !isLoading && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={5}
                  className="py-10 text-center text-content-danger"
                >
                  Couldn’t load receipts.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && receipts.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={5}
                  className="py-10 text-center text-content-secondary"
                >
                  No receipts yet. Upload one above.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
